import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateBlog, deleteBlog } from '../../services/blogService';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import StatusBadge from '../../components/admin/StatusBadge';
import CardSkeleton from '../../components/admin/CardSkeleton';

const AdminBlogsList = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [likesData, setLikesData] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    // Set up real-time listeners for blogs and likes
    const blogsQuery = query(collection(db, 'blogs'), orderBy('createdAt', 'desc'));
    const likesQuery = query(collection(db, 'likes'), orderBy('likedAt', 'desc'));

    const unsubscribeBlogs = onSnapshot(blogsQuery, (blogsSnapshot) => {
      const blogsData = blogsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBlogs(blogsData);
      setLoading(false);
    });

    const unsubscribeLikes = onSnapshot(likesQuery, (likesSnapshot) => {
      const likesData = likesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLikesData(likesData);
    });

    // Cleanup function
    return () => {
      unsubscribeBlogs();
      unsubscribeLikes();
    };
  }, []);

  useEffect(() => {
    filterBlogs();
  }, [blogs, likesData, searchTerm, statusFilter, categoryFilter, sortBy]);


  const filterBlogs = () => {
    // Combine blogs with likes data
    const likesCount = {};
    likesData.forEach(like => {
      likesCount[like.blogId] = (likesCount[like.blogId] || 0) + 1;
    });

    let filtered = blogs.map(blog => ({
      ...blog,
      likes: likesCount[blog.id] || 0
    }));

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(blog =>
        blog.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(blog => blog.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(blog => blog.category === categoryFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'likes':
          return b.likes - a.likes; // Descending for likes
        case 'created':
        default:
          const aDate = a.createdAt?.seconds || 0;
          const bDate = b.createdAt?.seconds || 0;
          return bDate - aDate; // Newest first
      }
    });

    setFilteredBlogs(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const getUniqueCategories = () => {
    const categories = blogs.map(blog => blog.category).filter(Boolean);
    return [...new Set(categories)];
  };

  const handleStatusChange = async (blogId, newStatus) => {
    try {
      await updateBlog(blogId, { status: newStatus });
      // Data will update automatically via real-time listeners
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      try {
        await deleteBlog(blogId);
        // Data will update automatically via real-time listeners
      } catch (err) {
        setError(err.message);
      }
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBlogs = filteredBlogs.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return <CardSkeleton height="500px" />;
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Error Loading Blogs</h4>
        <p>{error}</p>
        <button className="btn btn-outline-danger" onClick={() => setError(null)}>
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Blog Posts</h2>
        <button
          className="btn btn-primary btn-admin"
          onClick={() => navigate('/admin/blogs/new')}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Create New Post
        </button>
      </div>

      {/* Filters */}
      <div className="card shadow-sm border-0 rounded-3 mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search by title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">All Categories</option>
                {getUniqueCategories().map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="created">Sort by Date</option>
                <option value="title">Sort by Title</option>
                <option value="status">Sort by Status</option>
                <option value="category">Sort by Category</option>
                <option value="likes">Sort by Likes</option>
              </select>
            </div>
            <div className="col-md-3">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setCategoryFilter('all');
                  setSortBy('created');
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="mb-3 text-muted">
        Showing {paginatedBlogs.length} of {filteredBlogs.length} blog posts
      </div>

      {/* Table */}
      <div className="admin-table">
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Category</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Likes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedBlogs.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-5 text-muted">
                  <i className="bi bi-file-earmark-x fs-1 mb-3 d-block"></i>
                  No blog posts found matching your criteria.
                </td>
              </tr>
            ) : (
              paginatedBlogs.map(blog => (
                <tr key={blog.id}>
                  <td>
                    <div className="fw-medium text-truncate" style={{ maxWidth: '250px' }}>
                      {blog.title}
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={blog.status} type="blog" />
                  </td>
                  <td>{blog.category || 'Uncategorized'}</td>
                  <td>{formatDate(blog.createdAt)}</td>
                  <td>{formatDate(blog.updatedAt)}</td>
                  <td>
                    <span className="badge" style={{ backgroundColor: '#5a6f56', color: 'white' }}>
                      <i className="bi bi-heart me-1"></i>
                      {blog.likes || 0}
                    </span>
                  </td>
                  <td>
                    <div className="btn-group" role="group">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/admin/blogs/${blog.id}/edit`)}
                        title="Edit"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => navigate(`/blog/${blog.slug}`)}
                        title="View"
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                      {blog.status === 'draft' && (
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => handleStatusChange(blog.id, 'published')}
                          title="Publish"
                        >
                          <i className="bi bi-check-circle"></i>
                        </button>
                      )}
                      {blog.status === 'published' && (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => handleStatusChange(blog.id, 'draft')}
                          title="Unpublish"
                        >
                          <i className="bi bi-x-circle"></i>
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(blog.id)}
                        title="Delete"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
            </li>
            {[...Array(totalPages)].map((_, index) => (
              <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default AdminBlogsList;